import { Braces, Brackets, Play, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { LocalTime } from '~/components/common/LocalTime';
import type { PlacementTestDataTab } from '~/components/pages/ai/placements/TestDataModal';
import renderPlacementComponentPreview from '~/components/pages/ai/placements/preview/registry';
import type { PlacementRunAi } from '~/components/pages/ai/placements/preview/TextField';
import { Alert, Badge, Button } from '~/components/ui';
import { buildAiAssistInstruction, promptAssistErrorStatus } from '~/lib/ai/promptAssist';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { formatDuration } from '~/lib/format';
import { parseJsonRecord } from '~/lib/ai/placementValidation';
import { useRunPlacementTestMutation } from '~/modules/query/ai/placements';
import type { PlacementTestResult } from '~/modules/query/ai/types';

interface PlacementTestWorkspaceProps {
  /** The saved placement's key. A test always runs the saved version, never unsaved edits. */
  placementKey: string;
  componentId: string;
  componentVersion: string;
  sampleContext: string;
  sampleProps: string;
  hasUnsavedChanges: boolean;
  onOpenTestData: (tab: PlacementTestDataTab) => void;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const tokenStats = (result: PlacementTestResult) => [
  { label: 'Prompt tokens', value: result.usage.promptTokens },
  { label: 'Completion tokens', value: result.usage.completionTokens },
  { label: 'Total tokens', value: result.usage.totalTokens },
  { label: 'Duration', value: formatDuration(result.usage.durationMs) },
];

/**
 * The live preview and test panel: renders the placement's component, sends the sample
 * data to the service, and shows the compiled prompt and output. The `{}` and `[]`
 * buttons open the sample data editors in `TestDataModal`.
 */
export default function PlacementTestWorkspace({
  placementKey,
  componentId,
  componentVersion,
  sampleContext,
  sampleProps,
  hasUnsavedChanges,
  onOpenTestData,
}: PlacementTestWorkspaceProps) {
  const [runTest, { isLoading }] = useRunPlacementTestMutation();
  const [result, setResult] = useState<PlacementTestResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const runRef = useRef(0);

  const parsedContext = parseJsonRecord(sampleContext, 'Sample context');
  const parsedProps = parseJsonRecord(sampleProps, 'Sample props');

  // A different placement's saved result must never show under this one.
  useEffect(() => {
    setResult(null);
    setRunError(null);
  }, [placementKey]);

  const runAi: PlacementRunAi = async (request) => {
    if (!parsedContext.success) {
      throw new Error(parsedContext.error);
    }

    if (!parsedProps.success) {
      throw new Error(parsedProps.error);
    }

    const instruction = buildAiAssistInstruction(request.action, request.userInstruction);

    try {
      const response = await runTest({
        placementKey,
        sampleContext: parsedContext.data,
        sampleProps: parsedProps.data,
        user: isRecord(parsedContext.data.user) ? parsedContext.data.user : undefined,
        userInstructions: {
          action: request.action,
          inputText: request.inputText,
          outputFormat: request.outputFormat,
          instruction,
          userInstruction: request.userInstruction,
        },
      }).unwrap();

      setResult(response.result);

      if (response.result.status === 'failed') {
        // The run panel shows the service's own error text; a toast gets a sentence.
        throw new Error('The AI could not generate a draft right now. Please try again.');
      }

      return {
        outputText: response.result.prompt.output,
        compiledPrompt: response.result.prompt.compiled,
        reasoning: response.result.prompt.reasoning,
        usage: response.result.usage,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }

      // Keep the HTTP status so a 5xx is sanitised instead of shown as service text.
      throw Object.assign(
        new Error(apiErrorMessage(error as ApiErrorInput) ?? 'The AI request failed.'),
        {
          status: promptAssistErrorStatus(error),
        },
      );
    }
  };

  const runStandaloneTest = async () => {
    runRef.current += 1;
    const requestId = runRef.current;
    setRunError(null);

    if (!parsedContext.success) {
      setRunError(parsedContext.error);
      return;
    }

    if (!parsedProps.success) {
      setRunError(parsedProps.error);
      return;
    }

    try {
      const response = await runTest({
        placementKey,
        sampleContext: parsedContext.data,
        sampleProps: parsedProps.data,
        user: isRecord(parsedContext.data.user) ? parsedContext.data.user : undefined,
      }).unwrap();

      if (runRef.current !== requestId) {
        return;
      }

      setResult(response.result);

      if (response.result.status === 'failed') {
        setRunError(response.result.error ?? 'The test ran but the model returned an error.');
      }
    } catch (error) {
      if (runRef.current === requestId) {
        setRunError(
          apiErrorMessage(error as ApiErrorInput) ?? 'The test could not run. Try again.',
        );
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Click through the live component. AI actions and the standalone test both use the saved
          placement.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            ariaLabel="Edit sample context"
            title="Test data: context"
            onClick={() => onOpenTestData('context')}
            testId="ai-placement-test-data-context"
          >
            <Braces className="size-4" aria-hidden="true" />
            <span className="font-mono">{'{}'}</span>
          </Button>
          <Button
            variant="outline"
            ariaLabel="Edit sample props"
            title="Test data: props"
            onClick={() => onOpenTestData('props')}
            testId="ai-placement-test-data-props"
          >
            <Brackets className="size-4" aria-hidden="true" />
            <span className="font-mono">[]</span>
          </Button>
          <Button
            variant="outline"
            startIcon={<Play className="size-4" aria-hidden="true" />}
            loading={isLoading}
            onClick={() => void runStandaloneTest()}
            testId="ai-placement-run-test"
          >
            Run test
          </Button>
        </div>
      </div>

      {hasUnsavedChanges ? (
        <Alert variant="warning">
          <p className="text-sm text-gray-800 dark:text-white/90">
            Tests run the saved version. Save your changes before running a test to include them.
          </p>
        </Alert>
      ) : null}

      {!parsedContext.success ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">
            {parsedContext.error}. Fix the sample context before running a test.
          </p>
        </Alert>
      ) : null}

      {!parsedProps.success ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">
            {parsedProps.error}. Fix the sample props before running a test.
          </p>
        </Alert>
      ) : null}

      {runError ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">{runError}</p>
        </Alert>
      ) : null}

      {renderPlacementComponentPreview({
        componentId,
        componentVersion,
        placementKey,
        isDisabled: false,
        aiDisabled: false,
        isRunning: isLoading,
        onRunAi: runAi,
        sampleContext: parsedContext.success ? parsedContext.data : {},
        sampleProps: parsedProps.success ? parsedProps.data : {},
      })}

      {result ? (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge color={result.status === 'passed' ? 'success' : 'error'} size="sm">
                {result.status === 'passed' ? 'Passed' : 'Failed'}
              </Badge>
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">Latest test</p>
            </div>
            <LocalTime
              value={result.createdAt}
              className="text-xs text-gray-500 dark:text-gray-400"
            />
          </div>

          {result.error ? (
            <p className="text-sm text-error-600 dark:text-error-400">{result.error}</p>
          ) : null}

          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {tokenStats(result).map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-800"
              >
                <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="mt-1 text-sm font-medium text-gray-800 dark:text-white/90">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          <details className="rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
            <summary className="cursor-pointer list-none text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 [&::-webkit-details-marker]:hidden">
              Compiled prompt
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs text-gray-800 dark:text-gray-200">
              {result.prompt.compiled || '(no compiled prompt)'}
            </pre>
          </details>

          <details className="rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
            <summary className="cursor-pointer list-none text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 [&::-webkit-details-marker]:hidden">
              Output
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs text-gray-800 dark:text-gray-200">
              {result.prompt.output || '(no output)'}
            </pre>
          </details>

          <details className="rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-800">
            <summary className="cursor-pointer list-none text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 [&::-webkit-details-marker]:hidden">
              Component payload
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto font-mono text-xs text-gray-800 dark:text-gray-200">
              {JSON.stringify(result.preview.payload, null, 2)}
            </pre>
          </details>
        </div>
      ) : (
        <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <RotateCcw className="size-3.5" aria-hidden="true" />
          Run a test to see the compiled prompt, tokens and output here.
        </p>
      )}
    </div>
  );
}
